import React, { Component } from "react";
import PropTypes from "prop-types";
import { createRefetchContainer, graphql } from "react-relay";
import moment from "moment";
import styled from "styled-components";
import DocumentTitle from "react-document-title";
import { seconds } from "metrick/duration";

import AgentJobRow from "~/components/agent/AgentJobRow";
import agentStopMutation from "~/mutations/AgentStop";
import Badge from "~/components/shared/Badge";
import Button from "~/components/shared/Button";
import Dialog from "~/components/shared/Dialog";
import Emojify from "~/components/shared/Emojify";
import FlashesStore from "~/stores/FlashesStore";
import FormTextarea from "~/components/shared/FormTextarea";
import FormTextField from "~/components/shared/FormTextField";
import FriendlyTime from "~/components/shared/FriendlyTime";
import Icon from "~/components/shared/Icon";
import PageWithContainer from "~/components/shared/PageWithContainer";
import Panel from "~/components/shared/Panel";
import PausedAgentNotice from "~/components/shared/PausedAgentNotice";
import PausedClusterQueuesNotice from "~/components/shared/PausedClusterQueuesNotice";
import permissions from "~/lib/permissions";
import { AgentIcon } from "~/components/job/Job/Agent";

const ExtrasTable = styled.table`
  @media (max-width: 720px) {
    &,
    tbody {
      display: block;
    }

    tr {
      display: flex;
    }

    th {
      padding-bottom: 0;
    }
  }
`;

// eslint-disable-next-line react/require-optimization
class AgentShow extends Component {
  static propTypes = {
    agent: PropTypes.shape({
      id: PropTypes.string,
      uuid: PropTypes.string,
      name: PropTypes.string,
      connectionState: PropTypes.string,
      disconnectedAt: PropTypes.string,
      stoppedAt: PropTypes.string,
      stoppedBy: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
      job: PropTypes.object,
      permissions: PropTypes.shape({
        agentStop: PropTypes.shape({
          allowed: PropTypes.bool,
        }),
        agentPause: PropTypes.shape({
          allowed: PropTypes.bool,
        }),
        agentResume: PropTypes.shape({
          allowed: PropTypes.bool,
        }),
      }),
      organization: PropTypes.shape({
        name: PropTypes.string,
        slug: PropTypes.string,
      }),
      clusterQueue: PropTypes.shape({
        key: PropTypes.string.isRequired,
        cluster: PropTypes.shape({
          uuid: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
        }).isRequired,
        uuid: PropTypes.string.isRequired,
        dispatchPaused: PropTypes.bool.isRequired,
        dispatchPausedAt: PropTypes.string,
        dispatchPausedBy: PropTypes.shape({
          name: PropTypes.string.isRequired,
        }),
        dispatchPausedNote: PropTypes.string,
      }),
      public: PropTypes.bool.isRequired,
      paused: PropTypes.bool.isRequired,
      pausedAt: PropTypes.string,
      pausedBy: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
      pausedNote: PropTypes.string,
      pausedTimeoutInMinutes: PropTypes.number,
    }),
    relay: PropTypes.object.isRequired,
  };

  state = {
    stopping: false,
    forceStopping: false,
  };

  componentDidMount() {
    // Only bother setting up the delayed load and refresher if we've got an
    // actual agent to play with.
    if (this.props.agent && this.props.agent.uuid) {
      // This will cause a full refresh of the data every 3 seconds. This seems
      // very low, but chances are people aren't really looking at this page
      // for long periods of time.
      this.startTimeout();
    }
  }

  componentWillUnmount() {
    clearTimeout(this._agentRefreshTimeout);
  }

  refetchVariables = () => {
    return {
      agentId: this.props.agent.id,
    };
  };

  startTimeout = () => {
    this._agentRefreshTimeout = setTimeout(this.fetchUpdatedData, seconds.bind(3));
  };

  fetchUpdatedData = () => {
    this.props.relay.refetch(
      this.refetchVariables(),
      null,
      (error) => {
        if (!error) {
          this.startTimeout();
        }
      },
      { force: true },
    );
  };

  renderExtraItem(title, content, { borderBottom = true, fullWidth = false } = {}) {
    return (
      <tr key={title} className={`${borderBottom && "border-gray border-b"} flex`}>
        <th className="semi-bold flex-none p-4 text-left align-top text-sm" width={120}>
          {title}
        </th>
        <td
          className={
            fullWidth ? "min-w-0 flex-auto p-4 text-sm" : "min-w-0 max-w-sm flex-auto p-4 text-sm"
          }
        >
          {content}
        </td>
      </tr>
    );
  }

  renderExtras(agent) {
    const extras = [];

    if (agent.clusterQueue) {
      extras.push(
        this.renderExtraItem(
          "Cluster",
          <a
            className="color-inherit"
            href={`/organizations/${this.props.agent.organization.slug}/clusters/${agent.clusterQueue.cluster.uuid}/queues`}
          >
            <Emojify text={agent.clusterQueue.cluster.name} />
          </a>,
        ),
      );

      extras.push(
        this.renderExtraItem(
          "Queue",
          <div className="flex items-center gap-1">
            <a
              className="color-inherit"
              href={`/organizations/${this.props.agent.organization.slug}/clusters/${agent.clusterQueue.cluster.uuid}/queues/${agent.clusterQueue.uuid}`}
            >
              {agent.clusterQueue.key}
            </a>

            {this.renderHostedBadge()}

            {agent.clusterQueue.dispatchPaused && (
              <span data-icon="paused-large">
                <Icon icon="paused-large" />
              </span>
            )}
          </div>,
        ),
      );
    }

    if (this.props.params.deletedClusterQueue) {
      {
        this.props.params.deletedClusterQueue.cluster.deleted
          ? extras.push(
              this.renderExtraItem(
                "Cluster",
                <div>
                  <Emojify text={this.props.params.deletedClusterQueue.cluster.name} />
                  <Badge
                    outline={true}
                    className="badge--gray"
                    // @ts-expect-error - TS2769 - No overload matches this call.
                    title="This cluster no longer exists."
                  >
                    Deleted
                  </Badge>
                </div>,
              ),
            )
          : extras.push(
              this.renderExtraItem(
                "Cluster",
                <a
                  className="color-inherit"
                  href={`/organizations/${this.props.agent.organization.slug}/clusters/${this.props.params.deletedClusterQueue.cluster.uuid}/queues`}
                >
                  <Emojify text={this.props.params.deletedClusterQueue.cluster.name} />
                </a>,
              ),
            );
      }

      extras.push(
        this.renderExtraItem(
          "Queue",
          <div>
            {this.props.params.deletedClusterQueue.key}
            <Badge
              outline={true}
              className="badge--gray"
              // @ts-expect-error - TS2769 - No overload matches this call.
              title="This cluster queue no longer exists."
            >
              Deleted
            </Badge>
          </div>,
        ),
      );
    }

    extras.push(
      this.renderExtraItem(
        "Agent State",
        <div className="flex">
          <div
            className={`letter-spacing-3 state-pill font-semibold uppercase tracking-widest ${this.getClassNameForStatePill(
              agent.connectionState,
            )}`}
          >
            {this.getLabelForStatePill(agent.connectionState)}
          </div>
        </div>,
      ),
    );

    extras.push(
      this.renderExtraItem(
        "Version",
        agent.version ? (
          <a
            href={`https://github.com/buildkite/agent/releases/tag/v${agent.version}`}
            className="lime hover-underline no-underline"
          >
            {agent.version}
          </a>
        ) : (
          "Unknown"
        ),
      ),
    );

    if (agent.version && agent.versionHasKnownIssues) {
      extras.push(
        this.renderExtraItem(
          "Upgrade Required ⚠️",
          <>
            buildkite-agent v{agent.version} has known issues and should be upgraded to a later
            version. See the{" "}
            <a
              href={`https://github.com/buildkite/agent/releases/tag/v${agent.version}`}
              className="lime hover-underline no-underline"
            >
              release notes on GitHub
            </a>{" "}
            for more information.
          </>,
        ),
      );
    }

    if (agent.hostname) {
      extras.push(
        this.renderExtraItem(
          "Hostname",
          <div className="max-w-full truncate">{agent.hostname}</div>,
        ),
      );
    }

    if (agent.pid) {
      extras.push(this.renderExtraItem("PID", agent.pid));
    }

    if (agent.ipAddress) {
      extras.push(this.renderExtraItem("IP Address", agent.ipAddress));
    }

    if (agent.userAgent) {
      extras.push(this.renderExtraItem("User Agent", agent.userAgent));
    }

    if (agent.operatingSystem) {
      extras.push(
        this.renderExtraItem(
          "OS",
          <div className="flex items-center gap-1">
            <AgentIcon
              os={
                agent.clusterQueue?.hosted
                  ? agent.clusterQueue?.hostedAgents?.instanceShape?.machineType?.toLowerCase()
                  : agent.operatingSystem?.name?.toLowerCase()
              }
            />
            {agent.operatingSystem.name}
          </div>,
        ),
      );
    }

    if (
      agent.clusterQueue?.hosted &&
      agent.clusterQueue?.hostedAgents?.instanceShape?.architecture
    ) {
      extras.push(
        this.renderExtraItem(
          "Architecture",
          <Badge className="text-charcoal-700 bg-gray-100">
            {agent.clusterQueue.hostedAgents.instanceShape.architecture}
          </Badge>,
        ),
      );
    }

    if (agent.clusterQueue?.hosted && agent.clusterQueue?.hostedAgents?.instanceShape) {
      const { vcpu, memory } = agent.clusterQueue.hostedAgents.instanceShape;

      extras.push(
        this.renderExtraItem(
          "System",
          <Badge className="text-charcoal-700 bg-gray-100">
            {vcpu} vCPU · {memory} GB RAM
          </Badge>,
        ),
      );
    }

    if (agent.priority) {
      extras.push(this.renderExtraItem("Priority", agent.priority));
    }

    extras.push(
      this.renderExtraItem(this.renderAgentJobsLink("Jobs"), this.renderExtraJobs(agent), {
        fullWidth: true,
      }),
    );

    if (agent.connectedAt) {
      extras.push(
        this.renderExtraItem(
          "Connected",
          <FriendlyTime value={agent.connectedAt} capitalized={true} />,
        ),
      );
    }

    if (agent.connectionState === "connected") {
      if (agent.pingedAt) {
        extras.push(
          this.renderExtraItem(
            "Last Ping",
            <FriendlyTime value={agent.pingedAt} capitalized={true} />,
          ),
        );
      }
    }

    if (agent.connectionState === "disconnected") {
      extras.push(
        this.renderExtraItem("Disconnected", <FriendlyTime value={agent.disconnectedAt} />),
      );
    } else if (agent.connectionState === "lost") {
      extras.push(this.renderExtraItem("Last Ping", <FriendlyTime value={agent.lostAt} />));
      if (agent.disconnectedAt) {
        extras.push(
          this.renderExtraItem("Marked Lost", <FriendlyTime value={agent.disconnectedAt} />),
        );
      }
    } else if (agent.connectionState === "stopped" || agent.connectionState === "stopping") {
      if (agent.stoppedBy) {
        extras.push(
          this.renderExtraItem(
            "Stopped By",
            <span>
              {agent.stoppedBy.name} <FriendlyTime value={agent.stoppedAt} capitalized={false} />
            </span>,
          ),
        );
      }

      if (agent.disconnectedAt) {
        extras.push(
          this.renderExtraItem("Disconnected", <FriendlyTime value={agent.disconnectedAt} />),
        );
      }
    }

    let metaDataContent = "None";

    if (agent.metaData.length > 0) {
      // The sort() method modifies the array in place but agent.metaData is part
      // of the react props and props are frozen in dev (but not prod). So we take
      // a copy of the array before sorting.
      let metaDataList = Array.from(agent.metaData).sort();

      if (agent.clusterQueue) {
        const clusterQueueName = `queue=${agent.clusterQueue.key}`;
        metaDataList = metaDataList.filter((metadata) => metadata !== clusterQueueName);
      }

      if (metaDataList.length) {
        metaDataContent = metaDataList.map((metaData) => {
          return <div key={metaData}>{metaData}</div>;
        });
      }
    }

    extras.push(
      this.renderExtraItem("Tags", <pre className="code">{metaDataContent}</pre>, {
        borderBottom: false,
      }),
    );

    return (
      <ExtrasTable className="w-full">
        <tbody>{extras}</tbody>
      </ExtrasTable>
    );
  }

  clusterPath() {
    const {
      agent: { clusterQueue },
    } = this.props;
    if (!clusterQueue) {
      return "/unclustered";
    }

    return `/clusters/${clusterQueue.cluster.uuid}/queues/${clusterQueue.uuid}`;
  }

  jobsPath() {
    const { agent } = this.props;
    const orgPath = `/organizations/${agent.organization.slug}`;
    const clusterPath = this.clusterPath();

    if (this.props.params.deletedClusterQueue) {
      return `${orgPath}/agents/${agent.uuid}/jobs`;
    }

    return `${orgPath}${clusterPath}/agents/${agent.uuid}/jobs`;
  }

  pausePath() {
    const { agent } = this.props;
    const orgPath = `/organizations/${agent.organization.slug}`;
    const clusterPath = this.clusterPath();

    return `${orgPath}${clusterPath}/agents/${agent.uuid}/pause`;
  }

  resumePath() {
    const { agent } = this.props;
    const orgPath = `/organizations/${agent.organization.slug}`;
    const clusterPath = this.clusterPath();

    return `${orgPath}${clusterPath}/agents/${agent.uuid}/resume`;
  }

  renderAgentJobsButton(linkText) {
    const className = "btn btn-default mt-2";

    return (
      <Button href={this.jobsPath()} className={className}>
        {linkText}
      </Button>
    );
  }

  renderAgentJobsLink(linkText) {
    const className = "semi-bold black hover-lime no-underline";

    return (
      <a href={this.jobsPath()} className={className}>
        {linkText}
      </a>
    );
  }

  renderExtraJobs(agent) {
    if (agent.jobs.edges.length < 1) {
      return "This agent has not run any jobs";
    }

    return (
      <div>
        <Panel>
          {agent.jobs.edges.map(({ node: job }) => (
            <AgentJobRow key={job.uuid} job={job} jobType="command" />
          ))}
        </Panel>
        {this.renderAgentJobsButton("Show all jobs")}
      </div>
    );
  }

  handleStopButtonClick = (evt) => {
    evt.preventDefault();

    this.setState({ stopping: true }, () => {
      // We add a delay in case it executes so quickly that the user can't
      // understand what just flashed past their face.
      setTimeout(() => {
        this.sendAgentStopMutation(
          true,
          this.handleStopMutationSuccess,
          this.handleStopMutationError,
        );
      }, 1250);
    });
    this.handleAgentStopDialogClose();
  };

  handleForceStopButtonClick = (evt) => {
    evt.preventDefault();

    this.setState({ forceStopping: true }, () => {
      this.sendAgentStopMutation(
        false,
        this.handleForceStopMutationSuccess,
        this.handleForceStopMutationError,
      );
    });
  };

  sendAgentStopMutation = (graceful, onCompleted, onError) => {
    return agentStopMutation({
      id: this.props.agent.id,
      graceful,
      onCompleted,
      onError,
    });
  };

  handleStopMutationSuccess = () => {
    this.setState({ stopping: false });
  };

  handleStopMutationError = (response) => {
    const error = response.source.errors[0].message;
    FlashesStore.flash(FlashesStore.ERROR, error);

    this.setState({ stopping: false });
  };

  handleForceStopMutationSuccess = () => {
    // Force stopping state stays on, because it doesn't change the connectionState
  };

  handleForceStopMutationError = (response) => {
    const error = response.source.errors[0].message;
    FlashesStore.flash(FlashesStore.ERROR, error);

    this.setState({ forceStopping: false });
  };

  handleAgentStopDialogClose = () => {
    this.setState({ agentStopDialogOpen: false });
  };

  handleAgentStopDialogOpen = () => {
    this.setState({ agentStopDialogOpen: true });
  };

  handleAgentPauseDialogOpen = () => {
    this.setState({ agentPauseDialogOpen: true });
  };

  handleAgentPauseDialogClose = () => {
    this.setState({ agentPauseDialogOpen: false });
  };

  getClassNameForStatePill = (agentState) => {
    if (agentState === "connected") {
      return "state-pill-green";
    }
    if (agentState === "lost" || agentState === "stopping") {
      return "state-pill-pink";
    }
    return "state-pill-gray";
  };

  getLabelForStatePill = (agentState) => {
    return agentState.replace("_", " ");
  };

  render() {
    // If we don't have an agent object, or we do but it doesn't have an id
    // (perhaps Relay gave us an object but it's empty) then we can safely
    // assume that it's a 404.
    if (!this.props.agent || !this.props.agent.uuid) {
      return (
        <DocumentTitle title="Agents / No Agent Found">
          <PageWithContainer>
            <p>No agent could be found!</p>
          </PageWithContainer>
        </DocumentTitle>
      );
    }

    const agent = this.props.agent;

    return (
      <DocumentTitle
        title={`Agents / ${this.props.agent.name} · ${this.props.agent.organization.name}`}
      >
        <>
          {agent.clusterQueue?.dispatchPaused && (
            <PausedClusterQueuesNotice pausedQueues={[agent.clusterQueue]} />
          )}
          {agent.paused && <PausedAgentNotice agent={agent} />}

          <Panel className="mx-auto">
            <Panel.Header className="flex p-4">
              <div className="semi-bold" style={{ width: "120px" }}>
                Name
              </div>
              <div className="flex items-center gap-1">
                <div className="truncate font-normal">{this.props.agent.name}</div>
                {this.renderPublicBadge()}
              </div>
            </Panel.Header>

            <div key="info">
              {this.renderExtras(agent)}
              {(this.props.agent.connectionState === "connected" ||
                this.props.agent.connectionState === "stopping") && (
                <div className="px-4 pb-4">
                  <p className="m-0" style={{ marginLeft: "120px" }}>
                    You can use the agent’s tags to target the agent in your pipeline’s step
                    configuration, or to set the agent’s queue. See the{" "}
                    <a
                      className="lime hover-lime hover-underline no-underline"
                      href="/docs/agent/v3/cli-start"
                    >
                      Agent Tags and Queue Documentation
                    </a>{" "}
                    for more details.
                  </p>
                </div>
              )}
            </div>

            {this.renderStopRow()}
            {this.renderForceStopRow()}
            {this.renderPauseRow()}
          </Panel>
        </>
      </DocumentTitle>
    );
  }

  renderStopRow() {
    if (
      this.props.agent.connectionState === "disconnected" ||
      this.props.agent.connectionState === "lost" ||
      this.props.agent.connectionState === "stopped"
    ) {
      return null;
    }
    if (!permissions(this.props.agent.permissions).isPermissionAllowed("agentStop")) {
      return null;
    }

    const stopping = this.state.stopping || this.props.agent.connectionState === "stopping";

    return (
      <div className="p-4">
        <div className="flex items-center" style={{ marginLeft: "120px" }}>
          <Button
            loading={stopping ? "Stopping…" : false}
            onClick={this.handleAgentStopDialogOpen}
            className="mr-3 flex-none"
          >
            Stop Agent
          </Button>
          <Dialog
            isOpen={this.state.agentStopDialogOpen}
            onRequestClose={this.handleAgentStopDialogClose}
            width={450}
          >
            <div className="px-5 pb-5">
              <h2 className="text-xl">Are you sure?</h2>
              <p className="mb-3">
                This will send a signal to the agent that it should disconnect and will prevent it
                from running jobs.
              </p>
              <Button
                loading={stopping ? "Stopping…" : false}
                onClick={this.handleStopButtonClick}
                className="btn-danger mr-3"
                style={{ width: "100%" }}
              >
                <span>Yes, stop this agent</span>
              </Button>
            </div>
          </Dialog>
          <p className="dark-gray m-0 flex-1">
            {!this.props.agent.job &&
              !this.props.agent.stoppedAt &&
              "Send a signal to the agent that it should disconnect."}
            {this.props.agent.job &&
              !this.props.agent.stoppedAt &&
              "Send a signal to the agent that it should disconnect once its current job has completed."}
            {this.props.agent.job &&
              this.props.agent.stoppedAt &&
              "Waiting for the agent to complete its current job and disconnect."}
            {!this.props.agent.job &&
              this.props.agent.stoppedAt &&
              "Waiting for the agent to disconnect."}
            {!this.props.agent.job &&
              this.props.agent.stoppedAt &&
              moment().diff(this.props.agent.stoppedAt, "seconds") > 5 &&
              " If the agent doesn’t respond within a few minutes, it will be forcefully removed from your agent pool."}
          </p>
        </div>
      </div>
    );
  }

  renderForceStopRow() {
    if (this.state.stopping || this.props.agent.connectionState !== "stopping") {
      return null;
    }

    if (this.props.agent.stoppedAt && moment().diff(this.props.agent.stoppedAt, "seconds") < 5) {
      return null;
    }

    // if the user can't stop an agent, abort
    if (!permissions(this.props.agent.permissions).isPermissionAllowed("agentStop")) {
      return null;
    }

    return (
      <Panel.Row>
        <div className="flex items-center">
          <Button
            loading={this.state.forceStopping ? "Force Stopping…" : false}
            onClick={this.handleForceStopButtonClick}
            className="mb-1 mr-3 flex-none"
          >
            Force Stop Agent
          </Button>
          <p className="dark-gray m-0 flex-1">
            {!this.props.agent.job &&
              !this.state.forceStopping &&
              "Forcefully remove this agent from your agent pool."}
            {this.props.agent.job &&
              "Cancel the running job, and forcefully remove this agent from your agent pool."}
          </p>
        </div>
      </Panel.Row>
    );
  }

  renderPauseRow() {
    const { agent } = this.props;

    // We dont support pausing unclustered agents via the UI
    if (!agent.clusterQueue) {
      return null;
    }

    if (agent.connectionState === "stopped") {
      return null;
    }

    if (agent.paused) {
      if (!permissions(agent.permissions).isPermissionAllowed("agentResume")) {
        return null;
      }
    }

    if (!agent.paused) {
      if (!permissions(agent.permissions).isPermissionAllowed("agentPause")) {
        return null;
      }
    }

    return (
      <div className="p-4">
        <div className="flex items-center" style={{ marginLeft: "120px" }}>
          <Button onClick={this.handleAgentPauseDialogOpen} className="mr-3 flex-none">
            {agent.paused ? "Resume Agent" : "Pause Agent"}
          </Button>
          <Dialog
            isOpen={this.state.agentPauseDialogOpen}
            onRequestClose={this.handleAgentPauseDialogClose}
            width={450}
          >
            <form action={agent.paused ? this.resumePath() : this.pausePath()} method="post">
              <input type="hidden" name={window._csrf.param} value={window._csrf.token} />
              <div className="px-5 pb-5">
                <h2 className="text-xl">Are you sure?</h2>
                <p className="mb-3">
                  {agent.paused
                    ? "This will resume dispatch of jobs to this agent."
                    : "This will prevent dispatch of jobs to this agent."}
                </p>
                {!agent.paused && (
                  <>
                    <FormTextarea
                      name="agent[dispatch_paused_note]"
                      label="Note (optional)"
                      required={false}
                      help="e.g: Paused for maintenance"
                    />
                    <FormTextField
                      name="agent[timeout_in_minutes]"
                      label="Timeout"
                      defaultValue="5"
                      help="Number of minutes the agent will be paused before being automatically resumed (maximum of 1440 minutes). Defaults to 5 minutes if not specified."
                    />
                  </>
                )}

                <Button type="submit" className="btn-danger mr-3" style={{ width: "100%" }}>
                  <span>{agent.paused ? "Yes, resume this agent" : "Yes, pause this agent"}</span>
                </Button>
              </div>
            </form>
          </Dialog>
          <p className="dark-gray m-0 flex-1">
            {agent.paused
              ? "Resume dispatch of jobs to this agent."
              : "Pause dispatch of jobs to this agent."}
          </p>
        </div>
      </div>
    );
  }

  renderHostedBadge() {
    if (this.props.agent.clusterQueue?.hosted) {
      return (
        <Badge outline={true} className="inline-flex items-center gap-0.5 font-normal">
          <Icon icon="heroicons/16/solid/cloud" className="text-charcoal-300 size-4" />
          Hosted
        </Badge>
      );
    }
  }

  renderPublicBadge() {
    if (this.props.agent.public) {
      return (
        <Badge
          outline={true}
          className="very-dark-gray font-normal"
          title="Visible to everyone, including people outside this organization"
        >
          Public
        </Badge>
      );
    }
  }
}

/*
Disabling @graphql-eslint/no-deprecated here as I think there is some changes probably
required in the graph implementation so that we can actually use the alternatives to
`pingedAt`, `stoppedAt`, & `stoppedBy` (see schema.graphql - all of these fields have been
deprecated).
*/
export default createRefetchContainer(
  AgentShow,
  {
    agent: graphql`
      # eslint-disable @graphql-eslint/no-deprecated
      fragment AgentShow_agent on Agent {
        ...AgentStateIcon_agent
        id
        uuid
        name
        organization {
          name
          slug
        }
        clusterQueue {
          key
          cluster {
            uuid
            name
          }
          uuid
          dispatchPaused
          dispatchPausedAt
          dispatchPausedBy {
            name
            avatar {
              url
            }
          }
          dispatchPausedNote
          hosted
          hostedAgents {
            instanceShape {
              machineType
              vcpu
              memory
              architecture
            }
          }
        }
        connectedAt
        connectionState
        disconnectedAt
        hostname
        ipAddress
        job {
          ... on JobTypeCommand {
            state
            passed
          }
          ...AgentJobRow_job
        }
        jobs(first: 10) {
          edges {
            node {
              ... on JobTypeCommand {
                uuid
                state
                passed
              }
              ...AgentJobRow_job
            }
          }
          count
        }
        isRunningJob
        lostAt
        metaData
        operatingSystem {
          name
        }
        priority
        permissions {
          agentStop {
            allowed
            code
            message
          }
          agentPause {
            allowed
            code
            message
          }
          agentResume {
            allowed
            code
            message
          }
        }
        pid
        pingedAt
        public
        paused
        pausedAt
        pausedBy {
          name
          avatar {
            url
          }
        }
        pausedNote
        pausedTimeoutInMinutes
        stoppedAt
        stoppedBy {
          name
        }
        userAgent
        version
        versionHasKnownIssues
      }
    `,
  },
  graphql`
    query AgentShowRefetchQuery($agentId: ID!) {
      agent: node(id: $agentId) {
        ...AgentShow_agent
      }
    }
  `,
);
